import { isEmpty } from '@sgme/fp';
import classnames from 'classnames';
import { PayoffTypeSelector } from 'components/shared/ProductAndClientSelector/PayoffTypeSelector';
import { ProductFilters } from 'components/shared/ProductAndClientSelector/ProductFilters';
import { ProductSelectorEmptyState } from 'components/shared/ProductAndClientSelector/ProductSelectorEmptyState';
import type { IntlKey } from 'locales';
import type { SelectedProductVariation } from 'models/productsReferential';
import { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { getAvailableProductVariations } from 'state/selectors';
import type { AppState } from 'state/state.model';
import { workspaceSaveToLocalStorage } from 'state/workspaces/workspaces/workspace.actions';
import type { WorkspaceWithProductSelector } from 'state/workspaces/workspaces/workspaces.model';
import {
  getActivatedAssetTypeFilter,
  hasSeenProductSelectorDisclaimer,
} from 'state/workspaces/workspaces/workspaces.selector';

import { ProductSubTypeSelector } from './ProductSubTypeSelector';
import { ProductTypeSelector } from './ProductTypeSelector';
import { ProductWrapperSelector } from './ProductWrapperSelector';

interface ProductCategoryPickerProps {
  workspace: WorkspaceWithProductSelector;
  canSelectPayoffType?: boolean;
  selectedProductVariation: Partial<SelectedProductVariation>;
  onProductVariationSelected(product: Partial<SelectedProductVariation>, quickStart: boolean): void;
}

export const ProductCategoryPicker: FC<ProductCategoryPickerProps> = ({
  workspace,
  canSelectPayoffType = false,
  selectedProductVariation,
  onProductVariationSelected,
}) => {
  const dispatch = useDispatch();
  const shouldShowDisclaimer = useSelector((state: AppState) => !hasSeenProductSelectorDisclaimer(state, workspace));
  const activatedAssetType = useSelector((state: AppState) => getActivatedAssetTypeFilter(state, workspace));
  const availableProductVariations = useSelector((state: AppState) =>
    getAvailableProductVariations(state, workspace, activatedAssetType),
  );

  const onProductTypeSelected = useProductTypeSelectedHandler(onProductVariationSelected);
  const onProductSubTypeSelected = useProductSubTypeSelectedHandler(
    onProductVariationSelected,
    selectedProductVariation,
  );
  const onWrapperSelected = useOnWrapperSelectedHandler(onProductVariationSelected, selectedProductVariation);
  const onPayoffTypeSelected = useOnPayoffTypeSelected(onProductVariationSelected, selectedProductVariation);

  return (
    <div className={classnames('d-flex flex-column h-100')}>
      <ProductFilters workspace={workspace} />
      <div
        className="d-flex overflow-auto border-start border-end border-bottom rounded-1 rounded-top"
        style={{ flexBasis: '100px', flexGrow: 1 }}
      >
        {isEmpty(availableProductVariations) ? (
          <ProductSelectorEmptyState />
        ) : (
          <>
            <ProductTypeSelector
              className="col-4"
              workspace={workspace}
              availableProductVariations={availableProductVariations}
              selectedProductType={selectedProductVariation.productType}
              onProductTypeSelected={onProductTypeSelected}
            />
            <ProductSubTypeSelector
              className="col-5"
              workspace={workspace}
              availableProductVariations={availableProductVariations}
              selectedProductType={selectedProductVariation.productType}
              selectedProductSubType={selectedProductVariation.productSubType}
              onProductSubTypeSelected={onProductSubTypeSelected}
            />
            <ProductWrapperSelector
              className="col"
              workspace={workspace}
              availableProductVariations={availableProductVariations}
              selectedProductVariation={selectedProductVariation}
              onProductWrapperSelected={onWrapperSelected}
              onProductVariationQuickStartSelected={productVariation =>
                onProductVariationSelected(productVariation, true)
              }
            />
          </>
        )}
      </div>
      {canSelectPayoffType && (
        <PayoffTypeSelector
          workspace={workspace}
          selectedProductVariation={selectedProductVariation}
          onPayoffTypeSelected={onPayoffTypeSelected}
        />
      )}
      {workspace === 'dynamic' && (
        <div
          role="alert"
          className="alert alert-discreet-info alert-dismissible mt-3 fade show ps-2 py-2 fs-12 d-flex align-items-center"
        >
          <em className="icon icon-md icon-outlined me-2">playlist_add_check</em>
          <FormattedMessage tagName="p" id={`productSelector.${workspace}.isStatified` as IntlKey} />
        </div>
      )}
      {shouldShowDisclaimer && (
        <div
          role="alert"
          className="alert alert-discreet-info alert-dismissible mt-3 fade show ps-2 py-2 fs-12 d-flex align-items-center"
        >
          <em className="icon icon-md icon-outlined me-2">info</em>
          <FormattedMessage tagName="p" id={`productSelector.${workspace}.disclaimer` as IntlKey} />
          <button
            type="button"
            tabIndex={-1}
            className="btn-close"
            onClick={() => dispatch(workspaceSaveToLocalStorage(workspace, { hasSeenDisclaimer: true }))}
          />
        </div>
      )}
    </div>
  );
};

type OnProductVariationSelected = (product: Partial<SelectedProductVariation>, quickStart: boolean) => void;

function useProductTypeSelectedHandler(onProductVariationSelected: OnProductVariationSelected) {
  return useCallback(
    (productType: string) => onProductVariationSelected({ productType }, false),
    [onProductVariationSelected],
  );
}

function useProductSubTypeSelectedHandler(
  onProductVariationSelected: OnProductVariationSelected,
  selectedProductVariation: Partial<SelectedProductVariation>,
) {
  return useCallback(
    (productSubType: string) =>
      onProductVariationSelected({ productType: selectedProductVariation.productType, productSubType }, false),
    [onProductVariationSelected, selectedProductVariation.productType],
  );
}

function useOnWrapperSelectedHandler(
  onProductVariationSelected: OnProductVariationSelected,
  selectedProductVariation: Partial<SelectedProductVariation>,
) {
  return useCallback(
    (wrapper: string) =>
      onProductVariationSelected(
        {
          productType: selectedProductVariation.productType,
          productSubType: selectedProductVariation.productSubType,
          wrapper,
        },
        false,
      ),
    [onProductVariationSelected, selectedProductVariation.productSubType, selectedProductVariation.productType],
  );
}

function useOnPayoffTypeSelected(
  onProductVariationSelected: OnProductVariationSelected,
  selectedProductVariation: Partial<SelectedProductVariation>,
) {
  return useCallback(
    (payoffType: string) =>
      onProductVariationSelected(
        {
          productType: selectedProductVariation.productType,
          productSubType: selectedProductVariation.productSubType,
          wrapper: selectedProductVariation.wrapper,
          payoffType,
        },
        false,
      ),
    [
      onProductVariationSelected,
      selectedProductVariation.productSubType,
      selectedProductVariation.productType,
      selectedProductVariation.wrapper,
    ],
  );
}
